/**
 * Mirrors `services/engine/src/api/routes/sources.ts` and `.../connection.ts`.
 *
 * Same posture as `pipelines.ts` — Zod at the network boundary, one typed error
 * instead of an axios shape. What differs is the error surface: this flow's
 * failures are ones an operator can act on (a rejected token, a board someone
 * else already mirrors), so `SourceApiError` carries the engine's own words
 * rather than collapsing everything above 400 into `unknown`.
 */

import { z } from 'zod'
import type {
  EngineRole,
  LifecyclePhase,
  SlotMapping,
  SourceDiscovery,
  SourceSettings,
  SourceSettingsForm,
  StatusId,
} from '@ludus/core'
import { apiClient } from './client.ts'

/**
 * `kind` and `transport` are closed unions in core, but they are read here as plain
 * strings: mirroring the union would make a member added upstream reject the whole
 * discovery payload, blanking the wizard over one field it could simply not offer.
 */
const fieldSchema = z.object({
  id: z.string(),
  name: z.string(),
  kind: z.string(),
  options: z.array(z.object({ id: z.string(), name: z.string() })).optional(),
})

const discoverySchema = z.object({
  projects: z.array(
    z.object({
      externalRef: z.string(),
      name: z.string(),
      fields: z.array(fieldSchema),
    }),
  ),
  transports: z.object({
    available: z.array(z.string()),
    unavailable: z.array(z.object({ transport: z.string(), reason: z.string() })).optional(),
  }),
})

/**
 * `kind` is read as a plain string for the same reason a discovery field's is:
 * a kind added upstream must make the console skip one control, not blank the
 * whole form the operator is trying to fill in.
 */
const settingFieldSchema = z.object({
  key: z.string(),
  kind: z.string(),
  label: z.string(),
  hint: z.string().optional(),
  placeholder: z.string().optional(),
  required: z.boolean(),
})

const settingsFormSchema = z.object({
  scope: settingFieldSchema,
  credential: settingFieldSchema,
  extra: z.array(settingFieldSchema),
})

const connectionSchema = z.object({
  connection: z
    .object({
      adapterKind: z.string(),
      externalRef: z.string(),
      slotMapping: z.record(z.string().optional()),
      settings: z.record(z.string()).default({}),
    })
    .nullable(),
  credentialHint: z.string().nullable(),
  lastSyncAt: z.coerce.date().nullable(),
  lastSyncError: z.string().nullable(),
  lastSyncCounts: z.record(z.number()).nullable(),
})

const PHASES = ['intake', 'qualification', 'proposal', 'won', 'lost'] as const
const ROLES = ['arrived', 'awaiting_distribution', 'terminal_won', 'terminal_lost', 'none'] as const

const connectResultSchema = connectionSchema.extend({
  stages: z.array(
    z.object({
      externalKey: z.string(),
      statusId: z.string(),
      name: z.string(),
      lifecyclePhase: z.enum(PHASES),
      engineRole: z.enum(ROLES),
    }),
  ),
})

const kindsSchema = z.object({ items: z.array(z.string()) })

const RUN_STATES = ['queued', 'running', 'done', 'failed'] as const
const RUN_TRIGGERS = ['connect', 'cron', 'manual'] as const

const runSchema = z.object({
  id: z.string(),
  pipelineId: z.string(),
  state: z.enum(RUN_STATES),
  trigger: z.enum(RUN_TRIGGERS),
  processed: z.number(),
  total: z.number().nullable(),
  counts: z.record(z.number()),
  error: z.string().nullable(),
  startedAt: z.coerce.date().nullable(),
  finishedAt: z.coerce.date().nullable(),
  createdAt: z.coerce.date(),
})

const runEnvelopeSchema = z.object({ run: runSchema.nullable() })

const backfillJobSchema = z.object({
  id: z.string(),
  state: z.enum(RUN_STATES),
  from: z.coerce.date(),
  to: z.coerce.date(),
  cursor: z.string().nullable(),
  pages: z.number(),
  counts: z.record(z.number()),
  error: z.string().nullable(),
  attempts: z.number(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

const backfillStateSchema = z.object({
  job: backfillJobSchema.nullable(),
  backfilledFrom: z.coerce.date().nullable(),
  backfilledTo: z.coerce.date().nullable(),
})

export type BackfillJob = z.infer<typeof backfillJobSchema>
export type BackfillState = z.infer<typeof backfillStateSchema>

export interface SourceConnection {
  adapterKind: string
  externalRef: string
  slotMapping: SlotMapping
  settings: SourceSettings
}

export interface ConnectionState {
  connection: SourceConnection | null
  credentialHint: string | null
  lastSyncAt: Date | null
  lastSyncError: string | null
  lastSyncCounts: Record<string, number> | null
}

export type SyncRun = z.infer<typeof runSchema>

export interface ProvisionedStage {
  externalKey: string
  statusId: StatusId
  name: string
  lifecyclePhase: LifecyclePhase
  engineRole: EngineRole
}

export interface ConnectResult extends ConnectionState {
  stages: ProvisionedStage[]
}

export interface ConnectInput {
  adapterKind: string
  externalRef: string
  slotMapping: SlotMapping
  /** Answers to the adapter's own `extra` fields, stored on the connection. */
  settings: SourceSettings
  /** Omitted on a re-map: the engine keeps the stored credential (ADR-0004 §2). */
  credential?: string
}

export class SourceApiError extends Error {
  readonly kind:
    | 'unauthorized'
    | 'not_found'
    | 'invalid_input'
    | 'source_in_use'
    | 'unmirrored_leads'
    | 'source_unavailable'
    | 'stages_not_provisioned'
    | 'network'
    | 'unknown'
  readonly status: number | null
  /** The engine's own words, already written for an operator — never a status code. */
  readonly detail: string | null
  /** Which pipeline already mirrors the board, on `source_in_use`. */
  readonly heldBy: string | null
  /** How many cards no source reported stand in the way, on `unmirrored_leads`. */
  readonly count: number | null

  constructor(args: {
    kind: SourceApiError['kind']
    status: number | null
    message: string
    detail?: string | null
    heldBy?: string | null
    count?: number | null
  }) {
    super(args.message)
    this.name = 'SourceApiError'
    this.kind = args.kind
    this.status = args.status
    this.detail = args.detail ?? null
    this.heldBy = args.heldBy ?? null
    this.count = args.count ?? null
  }
}

interface EngineErrorBody {
  error?: string
  reason?: string
  detail?: string
  pipelineId?: string
  count?: number
}

export function classifySourceError(err: unknown): SourceApiError {
  const e = err as { response?: { status?: number; data?: EngineErrorBody }; message?: string }
  const status = e.response?.status ?? null
  const body = e.response?.data ?? {}

  if (status === null) {
    return new SourceApiError({ kind: 'network', status, message: e.message ?? 'network error' })
  }
  if (status === 401)
    return new SourceApiError({ kind: 'unauthorized', status, message: 'unauthorized' })
  if (status === 404) return new SourceApiError({ kind: 'not_found', status, message: 'not_found' })
  if (status === 409 && body.error === 'source_in_use') {
    return new SourceApiError({
      kind: 'source_in_use',
      status,
      message: 'source_in_use',
      heldBy: body.pipelineId ?? null,
    })
  }
  if (status === 409 && body.error === 'unmirrored_leads') {
    return new SourceApiError({
      kind: 'unmirrored_leads',
      status,
      message: 'unmirrored_leads',
      count: body.count ?? null,
    })
  }
  if (status === 400) {
    return new SourceApiError({
      kind: 'invalid_input',
      status,
      message: 'invalid_input',
      detail: body.reason ?? null,
    })
  }
  if (status === 502) {
    return new SourceApiError({
      kind:
        body.error === 'stages_not_provisioned' ? 'stages_not_provisioned' : 'source_unavailable',
      status,
      message: body.reason ?? 'adapter_unavailable',
      detail: body.detail ?? null,
    })
  }
  return new SourceApiError({ kind: 'unknown', status, message: e.message ?? `http ${status}` })
}

function castConnection(parsed: z.infer<typeof connectionSchema>): ConnectionState {
  return {
    connection:
      parsed.connection === null
        ? null
        : { ...parsed.connection, slotMapping: parsed.connection.slotMapping as SlotMapping },
    credentialHint: parsed.credentialHint,
    lastSyncAt: parsed.lastSyncAt,
    lastSyncError: parsed.lastSyncError,
    lastSyncCounts: parsed.lastSyncCounts,
  }
}

export async function listSourceKinds(): Promise<string[]> {
  try {
    const { data } = await apiClient.get('/sources')
    return kindsSchema.parse(data).items
  } catch (err) {
    throw classifySourceError(err)
  }
}

/**
 * What this source asks an operator for, in its own words. Fetched before the
 * first step renders, because the field list *is* the step — the console holds
 * no per-source labels of its own (ADR-0004 §2).
 */
export async function fetchSourceSettingsForm(kind: string): Promise<SourceSettingsForm> {
  try {
    const { data } = await apiClient.get(`/sources/${kind}/settings`)
    return settingsFormSchema.parse(data) as SourceSettingsForm
  } catch (err) {
    throw classifySourceError(err)
  }
}

export async function discoverSource(
  kind: string,
  input: { scope: string; credential: string; settings: SourceSettings },
): Promise<SourceDiscovery> {
  try {
    const { data } = await apiClient.post(`/sources/${kind}/discovery`, input)
    return discoverySchema.parse(data) as SourceDiscovery
  } catch (err) {
    throw classifySourceError(err)
  }
}

export async function fetchConnection(pipelineId: string): Promise<ConnectionState> {
  try {
    const { data } = await apiClient.get(`/pipelines/${pipelineId}/connection`)
    return castConnection(connectionSchema.parse(data))
  } catch (err) {
    throw classifySourceError(err)
  }
}

export async function connectSource(
  pipelineId: string,
  input: ConnectInput,
): Promise<ConnectResult> {
  try {
    const { data } = await apiClient.put(`/pipelines/${pipelineId}/connection`, input)
    const parsed = connectResultSchema.parse(data)
    return {
      ...castConnection(parsed),
      stages: parsed.stages.map((stage) => ({ ...stage, statusId: stage.statusId as StatusId })),
    }
  } catch (err) {
    throw classifySourceError(err)
  }
}

const disconnectReportSchema = z.object({
  retired: z.number().int().nonnegative(),
  runsCancelled: z.number().int().nonnegative(),
  keptForCards: z.number().int().nonnegative(),
  credentialDestroyed: z.boolean(),
})

export type DisconnectReport = z.infer<typeof disconnectReportSchema>

export type DisconnectResult =
  | { disconnected: true; report: DisconnectReport }
  | { disconnected: false; reason: 'not_connected' | 'sync_in_flight' }

const disconnectRefusalSchema = z.object({
  error: z.enum(['not_connected', 'sync_in_flight']),
})

/**
 * A refusal is an answer, not a failure — the same posture `deactivateStatus`
 * takes with its lead count: a sweep mid-page is a reason the dialog can show
 * and the operator can wait out.
 */
export async function disconnectSource(pipelineId: string): Promise<DisconnectResult> {
  try {
    const { data } = await apiClient.delete(`/pipelines/${pipelineId}/connection`)
    return { disconnected: true, report: disconnectReportSchema.parse(data) }
  } catch (err) {
    const res = (err as { response?: { status?: number; data?: unknown } }).response
    if (res?.status === 409) {
      const refusal = disconnectRefusalSchema.safeParse(res.data)
      if (refusal.success) return { disconnected: false, reason: refusal.data.error }
    }
    throw classifySourceError(err)
  }
}

export async function fetchSyncRun(pipelineId: string): Promise<SyncRun | null> {
  try {
    const { data } = await apiClient.get(`/pipelines/${pipelineId}/sync`)
    return runEnvelopeSchema.parse(data).run
  } catch (err) {
    throw classifySourceError(err)
  }
}

export async function startSync(pipelineId: string): Promise<SyncRun | null> {
  try {
    const { data } = await apiClient.post(`/pipelines/${pipelineId}/sync`)
    return runEnvelopeSchema.parse(data).run
  } catch (err) {
    throw classifySourceError(err)
  }
}

/** The pipeline's last period import and the interval its history covers (#231). */
export async function fetchBackfill(pipelineId: string): Promise<BackfillState> {
  try {
    const { data } = await apiClient.get(`/pipelines/${pipelineId}/backfill`)
    return backfillStateSchema.parse(data)
  } catch (err) {
    throw classifySourceError(err)
  }
}

export async function startBackfill(
  pipelineId: string,
  range: { from: Date; to: Date },
): Promise<BackfillState> {
  try {
    const { data } = await apiClient.post(`/pipelines/${pipelineId}/backfill`, {
      from: range.from.toISOString(),
      to: range.to.toISOString(),
    })
    return backfillStateSchema.parse(data)
  } catch (err) {
    throw classifySourceError(err)
  }
}
